import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import TextInput from '../../components/form-fields/text.input';
import Button from '../../components/button';
import useApi from '../../hooks/use-api';
import Notice from '../../components/notice';
import MainContainer from '../containers/main';
import { API_STATUS_FORBIDDEN, API_STATUS_VALIDATION_ERROR, MESSAGE_PASSWORD_INVALID } from '../../constants';
import {getCaptchaToken, resetCaptcha} from '../../hooks/use-recaptcha';

const PasswordResetConfirm = () => {
    const api = useApi();

    const { uid, token } = useParams();
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);

    const [captchaToken, setCaptchaToken] = useState(null);
    const [target, setTarget] = useState(null);

    const setRecaptchaToken = (reCaptchaToken) => {
        setCaptchaToken(reCaptchaToken);
    };

    useEffect(() => {
        // Submitting is done in use effect, to ensure the recaptcha token is ready
        if (!target || !captchaToken) {
            return;
        }

        api.passwordResetConfirm(target.new_password.value, target.confirm_password.value, uid, token, captchaToken).then(({ error: apiError }) => {
            if (apiError) {
                const { detail } = apiError;
                let apiErrorMessage;
                if (detail.new_password && detail.new_password.length) {
                    // eslint-disable-next-line prefer-destructuring
                    apiErrorMessage = detail.new_password[0];
                } else if (detail.length) {
                    // eslint-disable-next-line prefer-destructuring
                    apiErrorMessage = detail[0];
                } else {
                    apiErrorMessage = null;
                }
                setSubmitting(false);
                setTarget(null);
                resetCaptcha();
                let infoMessage;
                switch (apiError.status) {
                case API_STATUS_VALIDATION_ERROR:
                    if (apiErrorMessage === 'too_recent') {
                        infoMessage = 'The new password you entered has been used too recently.';
                    } else if (apiErrorMessage === 'failed_validation') {
                        infoMessage = MESSAGE_PASSWORD_INVALID;
                    } else if (apiErrorMessage && !apiErrorMessage.includes('_')) {
                        // The api appears to now return readable strings, so unless it's null or
                        // contains an _ (suggesting it's not readable), default to the returned string
                        infoMessage = apiErrorMessage;
                    } else {
                        infoMessage = 'Please enter a valid password.';
                    }
                    break;
                case API_STATUS_FORBIDDEN:
                    window.location = '/';
                    break;
                default:
                    infoMessage = 'An unexpected error occurred. Please try again later.';
                }

                setErrorMessage((
                    <Notice className="mt-2" color="yellow" icon="exclamation-orange">
                        <p>{ infoMessage }</p>
                    </Notice>
                ));
            } else {
                window.location = '/forgot-your-password/complete/';
            }
        });
    }, [captchaToken]);

    window.setRecaptchaToken = setRecaptchaToken;

    const handleSubmission = (e) => {
        setSubmitting(true);
        setErrorMessage(null);
        setTarget(e.target);
        getCaptchaToken();

        e.preventDefault();
    };

    return (
        <MainContainer>
            <>
                <h1>New password</h1>
                <p className="mt-4">Make sure your password isn’t too easy to guess. It must be at least 10 characters long and contain at least one numeric character and one alphabetic character.</p>
                { errorMessage }
                <form method="post" onSubmit={ handleSubmission }>
                    <TextInput
                        label="New password"
                        type="password"
                        className="mt-10"
                        name="new_password"
                    />
                    <TextInput
                        label="Confirm your new password"
                        type="password"
                        className="mt-10"
                        name="confirm_password"
                    />
                    <Button
                        text="Reset password"
                        className="mt-10 block btn--green-tint-3"
                        disabled={ submitting }
                        loader={ submitting }
                    />
                </form>
            </>
        </MainContainer>
    );
};

export default PasswordResetConfirm;
